import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, apiClient, API } from "../App";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { 
  Send, Bookmark, Download, AlertCircle, ChevronDown, ChevronUp, 
  FileText, ArrowLeft, Info, CheckCircle2, FileSearch, Upload, X, File
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { trackAskSubmit, trackPDFExport } from "../utils/analytics";

const AskCaseReview = () => {
  const { contractId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  // Shared state
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(contractId || "");
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [contractMetadata, setContractMetadata] = useState(null);
  const [showMetadata, setShowMetadata] = useState(true);
  
  // Mode state
  const [mode, setMode] = useState("standard"); // "standard" | "case_review"
  
  // Standard Ask state
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [askError, setAskError] = useState(null);
  
  // Case Review state
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [newCaseTitle, setNewCaseTitle] = useState("");
  const [caseFiles, setCaseFiles] = useState([]);
  const [caseSummary, setCaseSummary] = useState("");
  const [outputMode, setOutputMode] = useState("both");
  const [caseQuestion, setCaseQuestion] = useState("");
  const [caseAnswer, setCaseAnswer] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  useEffect(() => {
    fetchContracts();
  }, []);

  useEffect(() => {
    if (contractId) {
      setSelectedContract(contractId);
    }
  }, [contractId]);

  useEffect(() => {
    if (selectedContract) {
      fetchContractMetadata(selectedContract);
      if (mode === "case_review") {
        fetchCases();
      }
    }
  }, [selectedContract, mode]);

  const fetchContracts = async () => {
    try {
      const response = await apiClient.get('/contracts');
      setContracts(response.data);
      if (!contractId && response.data.length > 0) {
        setSelectedContract(response.data[0].id);
      }
    } catch (error) {
      console.error("Failed to load contracts:", error);
    }
  };

  const fetchContractMetadata = async (cId) => {
    try {
      const response = await apiClient.get(`/contracts/${cId}/metadata`);
      setContractMetadata(response.data);
    } catch (error) {
      console.error("Failed to load contract metadata:", error);
      setContractMetadata(null);
    }
  };

  const fetchCases = async () => {
    if (!selectedContract) return;
    try {
      const response = await apiClient.get(`/case-review/cases`, {
        params: { contract_id: selectedContract }
      });
      setCases(response.data);
    } catch (error) {
      console.error("Failed to load cases:", error);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => {
      const ext = f.name.toLowerCase().split('.').pop();
      return ['pdf', 'docx', 'txt'].includes(ext);
    });
    
    if (validFiles.length !== files.length) {
      toast.error("Some files were skipped. Only PDF, DOCX, and TXT files are supported.");
    }
    
    setCaseFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setCaseFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadCaseFiles = async () => {
    if (caseFiles.length === 0) {
      toast.error("Please select at least one case document");
      return;
    }
    
    if (!newCaseTitle.trim() && !selectedCase) {
      toast.error("Please enter a case title or select an existing case");
      return;
    }

    setUploadingFiles(true);
    
    try {
      const caseId = selectedCase || null;
      const caseTitle = selectedCase ? 
        cases.find(c => c.case_id === selectedCase)?.case_title : 
        newCaseTitle.trim();

      // Upload files one by one
      for (const file of caseFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('case_title', caseTitle);
        formData.append('contract_id', selectedContract);
        if (caseId) {
          formData.append('case_id', caseId);
        }

        await apiClient.post('/case-review/upload-file', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000
        });
      }

      toast.success(`${caseFiles.length} file(s) uploaded successfully`);
      setCaseFiles([]);
      setNewCaseTitle("");
      fetchCases(); // Refresh cases list
      
    } catch (error) {
      console.error("File upload failed:", error);
      toast.error(error.response?.data?.detail || "Failed to upload case files");
    } finally {
      setUploadingFiles(false);
    }
  };

  // Build a user-visible error from an axios error (429 limit vs generic)
  const buildAskError = (error) => {
    if (error.response) {
      const status = error.response.status;
      const detail = error.response.data?.detail;
      const detailMsg = (detail && typeof detail === 'object') ? detail.message
        : (typeof detail === 'string' ? detail : null);
      if (status === 429) {
        return {
          type: 'limit',
          message: detailMsg || "You've reached today's question limit. It resets at midnight UTC.",
          showUpgrade: true,
        };
      }
      return {
        type: 'error',
        message: detailMsg || "We couldn't answer that question. You were not charged — please try again.",
        showUpgrade: false,
      };
    }
    if (error.request) {
      return { type: 'error', message: "Request timed out. The question may be too complex or the server is busy. Please try again.", showUpgrade: false };
    }
    return { type: 'error', message: "Something went wrong. Please try again.", showUpgrade: false };
  };

  const handleStandardAsk = async () => {
    if (!selectedContract) {
      toast.error("Please select a contract");
      return;
    }
    if (!question.trim()) {
      toast.error("Please enter a question");
      return;
    }

    // Track question submission (anonymous)
    trackAskSubmit();

    setLoading(true);
    setAnswer(null);
    setAskError(null);
    
    const stillWorkingTimeout = setTimeout(() => {
      toast.info("Still working on your answer...", { duration: 5000 });
    }, 10000);
    
    try {
      const response = await apiClient.post('/questions/ask', {
        contract_id: selectedContract,
        question: question.trim()
      }, {
        timeout: 180000 // 3 minutes for complex questions
      });
      
      clearTimeout(stillWorkingTimeout);
      
      // Log the response structure for debugging
      console.log("[Q&A] API Response:", response.data);
      console.log("[Q&A] Response ID:", response.data?.id);
      console.log("[Q&A] Response type:", typeof response.data, Array.isArray(response.data) ? "array" : "object");
      
      // Defensive: Handle both direct object and array responses
      const answerData = Array.isArray(response.data) ? response.data[0] : response.data;
      
      if (!answerData?.id) {
        console.error("[Q&A] WARNING: Response missing ID field!", answerData);
        toast.error("Received invalid response from server. Please try again.");
        return;
      }
      
      setAnswer(answerData);
      
      const expanded = {};
      answerData.answer_sections.forEach((_, i) => {
        expanded[i] = true;
      });
      setExpandedSections(expanded);
      
    } catch (error) {
      clearTimeout(stillWorkingTimeout);
      console.error("[Q&A] Failed:", error);
      setAskError(buildAskError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCaseReviewAsk = async () => {
    if (!selectedContract) {
      toast.error("Please select a contract");
      return;
    }
    if (!selectedCase) {
      toast.error("Please select or create a case first");
      return;
    }
    if (!caseQuestion.trim()) {
      toast.error("Please enter a question");
      return;
    }

    setLoading(true);
    setCaseAnswer(null);
    setAskError(null);
    
    const stillWorkingTimeout = setTimeout(() => {
      toast.info("Analyzing case documents and CBA...", { duration: 5000 });
    }, 10000);
    
    try {
      const response = await apiClient.post('/case-review/ask', {
        contract_id: selectedContract,
        case_id: selectedCase,
        question_text: caseQuestion.trim(),
        case_summary: caseSummary.trim() || null,
        output_mode: outputMode
      }, {
        timeout: 120000 // 2 minutes for case review
      });
      
      clearTimeout(stillWorkingTimeout);
      
      // Log the response structure for debugging
      console.log("[Case Review] API Response:", response.data);
      console.log("[Case Review] Response ID:", response.data?.id);
      console.log("[Case Review] Response type:", typeof response.data, Array.isArray(response.data) ? "array" : "object");
      
      // Defensive: Handle both direct object and array responses
      const answerData = Array.isArray(response.data) ? response.data[0] : response.data;
      
      if (!answerData?.id) {
        console.error("[Case Review] WARNING: Response missing ID field!", answerData);
        toast.error("Received invalid response from server. Please try again.");
        return;
      }
      
      setCaseAnswer(answerData);
      
      const expanded = {};
      answerData.answer_sections.forEach((_, i) => {
        expanded[i] = true;
      });
      setExpandedSections(expanded);
      
    } catch (error) {
      clearTimeout(stillWorkingTimeout);
      console.error("[Case Review] Failed:", error);
      setAskError(buildAskError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCase = async (caseId) => {
    if (!confirm("Delete this case and all its documents? This cannot be undone.")) {
      return;
    }

    try {
      await apiClient.delete(`/case-review/cases/${caseId}`);
      toast.success("Case deleted");
      fetchCases();
      if (selectedCase === caseId) {
        setSelectedCase(null);
      }
    } catch (error) {
      console.error("Failed to delete case:", error);
    }
  };

  const handleSave = async () => {
    if (!answer) return;
    try {
      await apiClient.post(`/questions/${answer.id}/save`);
      setAnswer({ ...answer, is_saved: true });
      toast.success("Answer saved");
    } catch (error) {
      console.error("Failed to save answer:", error);
    }
  };

  const handleExport = async () => {
    console.log("[PDF Export] Starting export...");
    console.log("[PDF Export] answer object:", answer);
    
    if (!answer) {
      console.error("[PDF Export] No answer object");
      toast.error("No answer available to export");
      return;
    }
    
    console.log("[PDF Export] answer.id:", answer.id);
    console.log("[PDF Export] answer keys:", Object.keys(answer));
    
    if (!answer.id) {
      console.error("[PDF Export] Answer ID is missing");
      toast.error("Unable to export: Answer ID not found. Please save the answer first or access it from History page.");
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error("[PDF Export] No auth token");
        toast.error("Authentication required");
        return;
      }

      const exportUrl = `${API}/questions/${answer.id}/export-pdf`;
      console.log("[PDF Export] Fetching from:", exportUrl);
      
      const response = await fetch(exportUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      console.log("[PDF Export] Response status:", response.status);
      console.log("[PDF Export] Response headers:", response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[PDF Export] Error response:", errorText);
        let errorMessage = "Failed to download PDF";
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        toast.error(errorMessage);
        return;
      }

      const contentType = response.headers.get('content-type');
      console.log("[PDF Export] Content-Type:", contentType);
      
      if (!contentType || !contentType.includes('application/pdf')) {
        const responseText = await response.text();
        console.error('[PDF Export] Non-PDF response:', responseText);
        
        let errorMessage = "Server did not return a PDF file";
        try {
          const responseJson = JSON.parse(responseText);
          errorMessage = responseJson.detail || responseJson.message || errorMessage;
        } catch {}
        
        toast.error(errorMessage);
        return;
      }

      const blob = await response.blob();
      console.log("[PDF Export] Blob size:", blob.size);

      if (blob.size < 1000) {
        toast.error("PDF file is too small or corrupted");
        return;
      }

      let filename = 'CBAIQ_Answer.pdf';
      const disposition = response.headers.get('content-disposition');
      if (disposition) {
        const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      console.log("[PDF Export] Downloading as:", filename);

      // Track PDF export (anonymous)
      trackPDFExport();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success("PDF download started");
      console.log("[PDF Export] Success!");

    } catch (error) {
      console.error("[PDF Export] Exception:", error);
      toast.error("Failed to download PDF. Please try again.");
      console.error("[PDF Export] Error:", error);
    }
  };

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getRiskClass = (level) => {
    const lower = level?.toLowerCase();
    if (lower === "low") return "risk-low";
    if (lower === "medium") return "risk-medium";
    if (lower === "high") return "risk-high";
    return "bg-secondary text-muted-foreground";
  };

  const selectedContractData = contracts.find(c => c.id === selectedContract);

  const AskErrorBanner = () => askError && !loading ? (
    <div
      className={`border rounded-md p-4 flex items-start gap-3 ${askError.type === 'limit' ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-300'}`}
      data-testid="ask-error-banner"
    >
      <div className="flex-1">
        <p className={`text-sm font-medium ${askError.type === 'limit' ? 'text-amber-900' : 'text-red-900'}`} data-testid="ask-error-message">
          {askError.message}
        </p>
        {askError.showUpgrade && (
          <Link
            to="/pricing"
            className="inline-block mt-2 text-sm font-medium text-teal-700 underline underline-offset-2 hover:text-teal-900"
            data-testid="ask-error-upgrade-link"
          >
            Upgrade for a higher daily limit →
          </Link>
        )}
      </div>
    </div>
  ) : null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold mb-2">
            Ask a Question
          </h1>
          <p className="text-muted-foreground">
            Get guidance based on your contract
          </p>
        </div>

        {/* Mode Toggle */}
        <Tabs value={mode} onValueChange={setMode} className="mb-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="standard">Standard Ask</TabsTrigger>
            <TabsTrigger value="case_review">Case Review (Grievance)</TabsTrigger>
          </TabsList>

          {/* Standard Ask Mode */}
          <TabsContent value="standard" className="space-y-6 mt-6">
            {contracts.length === 0 ? (
              <div className="bg-card border border-border rounded-md p-6 text-center">
                <FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <h3 className="font-medium mb-2">No contracts uploaded</h3>
                <p className="text-sm text-muted-foreground mb-4">Upload a contract to start asking questions</p>
                <Link to="/upload">
                  <Button>Upload Contract</Button>
                </Link>
              </div>
            ) : (
              <>
                {/* Contract Selector */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Contract</label>
                  <Select value={selectedContract} onValueChange={setSelectedContract}>
                    <SelectTrigger className="bg-card">
                      <SelectValue placeholder="Select a contract" />
                    </SelectTrigger>
                    <SelectContent>
                      {contracts.map((contract) => (
                        <SelectItem key={contract.id} value={contract.id}>
                          {contract.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedContractData && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {selectedContractData.chunk_count} sections indexed
                    </p>
                  )}
                </div>

                {/* Contract Metadata */}
                {contractMetadata && (
                  <Collapsible 
                    open={showMetadata} 
                    onOpenChange={setShowMetadata}
                  >
                    <div className="border border-border bg-secondary/20 rounded-md overflow-hidden">
                      <CollapsibleTrigger className="w-full flex items-center justify-between p-4 hover:bg-secondary/40 transition-colors">
                        <div className="flex items-center gap-2">
                          <FileSearch className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium">Active Contract Info</span>
                          {contractMetadata.status === "ready" && (
                            <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                              <CheckCircle2 className="w-3 h-3" />
                              Ready
                            </span>
                          )}
                        </div>
                        {showMetadata ? (
                          <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className="px-4 pb-4 pt-2 space-y-3 text-sm">
                          <div>
                            <span className="text-muted-foreground">Name:</span>
                            <span className="ml-2 font-medium">{contractMetadata.name}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Indexed Sections:</span>
                            <span className="ml-2 font-medium">{contractMetadata.chunk_count}</span>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                )}

                {/* Question Input */}
                <div className="question-input-wrapper">
                  <Textarea
                    placeholder="Ask anything about your contract... e.g., 'Can I discipline someone for being late?'"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    className="min-h-[120px] pr-12 bg-card"
                    onKeyDown={(e) => {
                      if (e.isComposing || e.keyCode === 229) return;
                      
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (!loading && question.trim()) {
                          handleStandardAsk();
                        }
                      }
                    }}
                    data-testid="question-input"
                  />
                  <button
                    onClick={handleStandardAsk}
                    disabled={loading || !question.trim()}
                    className="question-submit-btn disabled:opacity-50"
                    data-testid="submit-question-btn"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>

                {/* Error / Limit Banner */}
                <AskErrorBanner />

                {/* Loading State */}
                {loading && (
                  <div className="bg-card border border-border rounded-md p-8 text-center">
                    <div className="animate-pulse">
                      <div className="h-4 bg-secondary rounded w-3/4 mx-auto mb-4" />
                      <div className="h-4 bg-secondary rounded w-1/2 mx-auto mb-4" />
                      <div className="h-4 bg-secondary rounded w-2/3 mx-auto" />
                    </div>
                    <p className="text-sm text-muted-foreground mt-4">Analyzing your contract...</p>
                  </div>
                )}

                {/* Answer Display */}
                {answer && !loading && (
                  <div className="space-y-4">
                    {answer.grounding_warning && (
                      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Information Note</p>
                          <p className="text-sm text-amber-700">{answer.grounding_warning}</p>
                        </div>
                      </div>
                    )}

                    <div className="bg-secondary/30 rounded-sm p-4">
                      <p className="text-sm text-muted-foreground mb-1">Your question:</p>
                      <p className="font-medium">{answer.question}</p>
                    </div>

                    {answer.answer_sections.map((section, index) => (
                      <Collapsible
                        key={index}
                        open={expandedSections[index]}
                        onOpenChange={() => toggleSection(index)}
                      >
                        <div className="answer-section">
                          <CollapsibleTrigger className="w-full flex items-center justify-between">
                            <h3 className="answer-section-title">{section.title}</h3>
                            {expandedSections[index] ? (
                              <ChevronUp className="w-4 h-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-muted-foreground" />
                            )}
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="answer-section-content mt-2 whitespace-pre-line">
                              {section.content}
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    ))}

                    {answer.citations && answer.citations.length > 0 && (
                      <div className="answer-section">
                        <h3 className="answer-section-title">Contract Citations</h3>
                        <div className="space-y-2 mt-2">
                          {answer.citations.map((citation, i) => (
                            <div key={i} className="citation-block citation-text">
                              {citation}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <span className={`risk-badge ${getRiskClass(answer.risk_level)}`}>
                        {answer.risk_level} Grievance Risk
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSave}
                          disabled={answer.is_saved}
                        >
                          <Bookmark className={`w-4 h-4 mr-1 ${answer.is_saved ? "fill-current" : ""}`} />
                          {answer.is_saved ? "Saved" : "Save"}
                        </Button>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Info className="w-4 h-4" />
                          <span>Export PDF from History or Saved pages</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Case Review Mode */}
          <TabsContent value="case_review" className="space-y-6 mt-6">
            {contracts.length === 0 ? (
              <div className="bg-card border border-border rounded-md p-6 text-center">
                <FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <h3 className="font-medium mb-2">CBA Required</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Case Review Mode requires an active Collective Bargaining Agreement to analyze grievances
                </p>
                <Link to="/upload">
                  <Button>Upload CBA</Button>
                </Link>
              </div>
            ) : (
              <>
                {/* Contract Selector */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Contract</label>
                  <Select value={selectedContract} onValueChange={setSelectedContract}>
                    <SelectTrigger className="bg-card">
                      <SelectValue placeholder="Select a contract" />
                    </SelectTrigger>
                    <SelectContent>
                      {contracts.map((contract) => (
                        <SelectItem key={contract.id} value={contract.id}>
                          {contract.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Info Banner */}
                <div className="border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-950/30 p-4 rounded-r">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">
                        Case Review Mode
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-400">
                        Upload grievance documents and get CBA-grounded violation analysis with draft responses. 
                        Authority (CBA) and evidence (case docs) are kept strictly separate.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Case File Upload Section */}
                <div className="border border-border rounded-md p-4 bg-card">
                  <h3 className="font-medium mb-3">Upload Case Documents</h3>
                  
                  {/* Existing Case or New Case */}
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Select Existing Case (Optional)
                      </label>
                      <Select value={selectedCase || "new"} onValueChange={(val) => setSelectedCase(val === "new" ? null : val)}>
                        <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Create new case..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Create new case...</SelectItem>
                          {cases.map((c) => (
                            <SelectItem key={c.case_id} value={c.case_id}>
                              {c.case_title} ({c.document_count} docs)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {!selectedCase && (
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          New Case Title
                        </label>
                        <Input
                          placeholder="e.g., 'Smith Grievance - Discipline'"
                          value={newCaseTitle}
                          onChange={(e) => setNewCaseTitle(e.target.value)}
                        />
                      </div>
                    )}
                  </div>

                  {/* File Input */}
                  <div className="mb-3">
                    <label className="block">
                      <div className="border-2 border-dashed border-border rounded-md p-4 text-center cursor-pointer hover:bg-secondary/50 transition-colors">
                        <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm font-medium mb-1">Click to select files</p>
                        <p className="text-xs text-muted-foreground">PDF, DOCX, TXT (max 10MB per file)</p>
                      </div>
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.docx,.txt"
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                    </label>
                  </div>

                  {/* Selected Files */}
                  {caseFiles.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {caseFiles.map((file, i) => (
                        <div key={i} className="flex items-center gap-2 bg-secondary/30 p-2 rounded">
                          <File className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm flex-1 truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(1)}KB
                          </span>
                          <button
                            onClick={() => removeFile(i)}
                            className="p-1 hover:bg-secondary rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload Button */}
                  <Button
                    onClick={handleUploadCaseFiles}
                    disabled={uploadingFiles || caseFiles.length === 0 || (!newCaseTitle.trim() && !selectedCase)}
                    className="w-full"
                  >
                    {uploadingFiles ? "Uploading..." : `Upload ${caseFiles.length} file(s)`}
                  </Button>
                </div>

                {/* Existing Cases */}
                {cases.length > 0 && (
                  <div className="border border-border rounded-md p-4 bg-card">
                    <h3 className="font-medium mb-3">Your Cases</h3>
                    <div className="space-y-2">
                      {cases.map((c) => (
                        <div 
                          key={c.case_id} 
                          className="flex items-center justify-between p-3 bg-secondary/30 rounded hover:bg-secondary/50 transition-colors"
                        >
                          <div>
                            <p className="font-medium text-sm">{c.case_title}</p>
                            <p className="text-xs text-muted-foreground">
                              {c.document_count} document(s) · {new Date(c.updated_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedCase(c.case_id)}
                            >
                              Select
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteCase(c.case_id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Case Review Question Form */}
                {selectedCase && (
                  <>
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Case Summary (Optional)
                      </label>
                      <Textarea
                        placeholder="Brief summary of the case facts..."
                        value={caseSummary}
                        onChange={(e) => setCaseSummary(e.target.value)}
                        className="min-h-[80px] bg-card"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Output Mode
                      </label>
                      <Select value={outputMode} onValueChange={setOutputMode}>
                        <SelectTrigger className="bg-card">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="both">Both (Analysis + Draft)</SelectItem>
                          <SelectItem value="analysis">Violation Analysis Only</SelectItem>
                          <SelectItem value="draft">Draft Response Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="question-input-wrapper">
                      <Textarea
                        placeholder="e.g., 'Is this a contract violation? Draft the response.'"
                        value={caseQuestion}
                        onChange={(e) => setCaseQuestion(e.target.value)}
                        className="min-h-[120px] pr-12 bg-card"
                        onKeyDown={(e) => {
                          if (e.isComposing || e.keyCode === 229) return;
                          
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (!loading && caseQuestion.trim()) {
                              handleCaseReviewAsk();
                            }
                          }
                        }}
                        data-testid="case-question-input"
                      />
                      <button
                        onClick={handleCaseReviewAsk}
                        disabled={loading || !caseQuestion.trim()}
                        className="question-submit-btn disabled:opacity-50"
                        data-testid="submit-case-question-btn"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Error / Limit Banner */}
                    <AskErrorBanner />

                    {/* Loading State */}
                    {loading && (
                      <div className="bg-card border border-border rounded-md p-8 text-center">
                        <div className="animate-pulse">
                          <div className="h-4 bg-secondary rounded w-3/4 mx-auto mb-4" />
                          <div className="h-4 bg-secondary rounded w-1/2 mx-auto mb-4" />
                          <div className="h-4 bg-secondary rounded w-2/3 mx-auto" />
                        </div>
                        <p className="text-sm text-muted-foreground mt-4">Analyzing case against CBA...</p>
                      </div>
                    )}

                    {/* Case Answer Display */}
                    {caseAnswer && !loading && (
                      <div className="space-y-4">
                        {caseAnswer.grounding_warning && (
                          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-amber-800">Information Note</p>
                              <p className="text-sm text-amber-700">{caseAnswer.grounding_warning}</p>
                            </div>
                          </div>
                        )}

                        <div className="bg-secondary/30 rounded-sm p-4">
                          <p className="text-sm text-muted-foreground mb-1">Your question:</p>
                          <p className="font-medium">{caseAnswer.question}</p>
                        </div>

                        {caseAnswer.answer_sections.map((section, index) => (
                          <Collapsible
                            key={index}
                            open={expandedSections[index]}
                            onOpenChange={() => toggleSection(index)}
                          >
                            <div className="answer-section">
                              <CollapsibleTrigger className="w-full flex items-center justify-between">
                                <h3 className="answer-section-title">{section.title}</h3>
                                {expandedSections[index] ? (
                                  <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                )}
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="answer-section-content mt-2 whitespace-pre-line">
                                  {section.content}
                                </div>
                              </CollapsibleContent>
                            </div>
                          </Collapsible>
                        ))}

                        {/* Contract Citations (CBA) */}
                        {caseAnswer.contract_citations && caseAnswer.contract_citations.length > 0 && (
                          <div className="answer-section">
                            <h3 className="answer-section-title">Contract Citations (Authority)</h3>
                            <div className="space-y-2 mt-2">
                              {caseAnswer.contract_citations.map((citation, i) => (
                                <div key={i} className="citation-block citation-text bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
                                  {citation}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Evidence Citations (Case Docs) */}
                        {caseAnswer.evidence_citations && caseAnswer.evidence_citations.length > 0 && (
                          <div className="answer-section">
                            <h3 className="answer-section-title">Evidence Citations (Case Documents)</h3>
                            <div className="space-y-2 mt-2">
                              {caseAnswer.evidence_citations.map((citation, i) => (
                                <div key={i} className="citation-block citation-text bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                                  {citation}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-4 border-t border-border">
                          <span className={`risk-badge ${getRiskClass(caseAnswer.risk_level)}`}>
                            {caseAnswer.risk_level} Grievance Risk
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  caseAnswer.answer_sections.find(s => s.title === "Draft Response")?.content || ""
                                );
                                toast.success("Draft response copied to clipboard");
                              }}
                            >
                              Copy Draft
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AskCaseReview;
